import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill, Stat } from "@/components/dash/ui";

const RISK_PILL: Record<string, string> = { low: "ok", medium: "warning", high: "critical" };
const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v.toFixed(1)}%`);

export default function RevenueConcentrationPage() {
  const q = useQuery({ queryKey: ["customer-concentration"], queryFn: dashApi.customerConcentration });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Revenue Concentration"
        description="How much of total customer revenue comes from the largest accounts. High concentration is a risk: losing one customer materially dents revenue."
        right={d && <StatusPill status={RISK_PILL[d.risk] ?? "info"} label={`${d.risk.toUpperCase()} risk`} />}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : d && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Total customer revenue" value={fmtCurrency(d.total_revenue)} sub={`${fmtNumber(d.customer_count)} customers`} />
            <Stat label="Top customer" value={pct(d.top1_pct)} sub="share of revenue" />
            <Stat label="Top 5" value={pct(d.top5_pct)} sub="share of revenue" />
            <Stat label="Top 10" value={pct(d.top10_pct)} sub="share of revenue" />
          </div>
          <Panel title={`Top ${d.top_n} customers`} subtitle={`These accounts represent ${pct(d.top_share_pct)} of all customer revenue.`}>
            {!d.top_customers.length ? <EmptyState compact>No revenue recorded yet.</EmptyState> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="pb-2 font-medium">#</th>
                    <th className="pb-2 font-medium">Customer</th>
                    <th className="pb-2 text-right font-medium">Spend</th>
                    <th className="pb-2 text-right font-medium">Share</th>
                    <th className="pb-2 text-right font-medium">Services</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-500/40">
                  {d.top_customers.map((c, i) => (
                    <tr key={c.id} className="text-slate-300 hover:bg-ink-700/40">
                      <td className="py-2 text-slate-500">{i + 1}</td>
                      <td className="py-2">
                        <Link to={`/customers/${c.id}`} className="text-slate-100 hover:text-brand-300">{c.name}</Link>
                        <div className="text-[11px] text-slate-600">{c.email ?? "no email"}</div>
                      </td>
                      <td className="py-2 text-right tabular-nums text-white">{fmtCurrency(c.total_spend)}</td>
                      <td className="py-2 text-right tabular-nums text-slate-400">{pct(c.share_pct)}</td>
                      <td className="py-2 text-right tabular-nums">{c.services_used}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
